import React, { memo, useCallback, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, Copy } from "lucide-react";

/**
 * Renders assistant output as Markdown.
 *
 * Security posture: `rehype-raw` is deliberately NOT enabled, so embedded HTML in
 * model output is rendered as text rather than injected into the DOM. Everything
 * below builds React elements — nothing reaches `dangerouslySetInnerHTML` — and
 * every URL passes through `safeUrl()`, which permits only http/https/mailto/tel
 * (plus inline image data URIs) and drops `javascript:` and friends.
 *
 * Only assistant messages are rendered this way. User text stays literal: their
 * own input echoed back as formatted markup is both surprising and a cheap way to
 * fake assistant-looking UI inside a user bubble.
 */

/** Schemes allowed for links. Anything else is dropped. */
const SAFE_LINK = /^(https?:|mailto:|tel:|#|\/)/i;
/** Images additionally allow inline raster/vector data URIs. */
const SAFE_IMAGE = /^(https?:|\/|data:image\/(png|jpe?g|gif|webp|avif|svg\+xml);base64,)/i;

const safeUrl = (url: string | undefined, pattern: RegExp): string | undefined => {
  if (!url) return undefined;
  const trimmed = url.trim();
  return pattern.test(trimmed) ? trimmed : undefined;
};

/**
 * Make a partially-streamed document safe to parse.
 *
 * Chunks arrive mid-token, so an odd number of code fences means the fence is
 * still open — left alone, the parser swallows the entire rest of the message
 * into a code block and the bubble visibly "breaks" on every other chunk.
 * Closing it for rendering keeps the output stable while text streams in.
 */
const balanceFences = (markdown: string): string => {
  const fences = (markdown.match(/^```/gm) ?? []).length;
  return fences % 2 === 1 ? `${markdown}\n\`\`\`` : markdown;
};

function CodeBlock({ language, code }: { language?: string; code: string }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    // `clipboard` is unavailable on insecure origins; fail quietly rather than
    // throwing inside a render tree that is streaming.
    navigator.clipboard?.writeText(code).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  }, [code]);

  return (
    <div className="group/code relative my-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-950 dark:border-dark-600">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {language || "code"}
        </span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-semibold text-slate-400 transition hover:bg-white/10 hover:text-slate-200"
          aria-label={copied ? "Copied" : "Copy code"}
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {/* Long lines scroll inside the block; the bubble never scrolls sideways. */}
      <pre className="overflow-x-auto p-3 text-[12.5px] leading-5">
        <code className="font-mono text-slate-100">{code}</code>
      </pre>
    </div>
  );
}

function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const [failed, setFailed] = useState(false);
  const url = safeUrl(src, SAFE_IMAGE);

  // A blocked or broken image still carries meaning in its alt text.
  if (!url || failed) {
    return (
      <span className="my-2 inline-block rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-400 dark:border-dark-600">
        {alt || "Image unavailable"}
      </span>
    );
  }

  return (
    <img
      src={url}
      alt={alt || ""}
      loading="lazy"
      onError={() => setFailed(true)}
      className="my-3 max-h-96 max-w-full rounded-xl border border-slate-200 object-contain dark:border-dark-600"
    />
  );
}

const components = {
  // ── block text ──────────────────────────────────────────────────────────
  p: ({ children }: any) => <p className="mb-3 last:mb-0">{children}</p>,

  h1: ({ children }: any) => (
    <h1 className="mb-2 mt-4 text-base font-bold first:mt-0">{children}</h1>
  ),
  h2: ({ children }: any) => (
    <h2 className="mb-2 mt-4 text-[15px] font-bold first:mt-0">{children}</h2>
  ),
  h3: ({ children }: any) => (
    <h3 className="mb-1.5 mt-3 text-sm font-bold first:mt-0">{children}</h3>
  ),
  h4: ({ children }: any) => (
    <h4 className="mb-1.5 mt-3 text-sm font-semibold first:mt-0">{children}</h4>
  ),
  h5: ({ children }: any) => (
    <h5 className="mb-1 mt-2 text-[13px] font-semibold first:mt-0">{children}</h5>
  ),
  h6: ({ children }: any) => (
    <h6 className="mb-1 mt-2 text-[13px] font-semibold uppercase tracking-wide text-slate-500 first:mt-0">
      {children}
    </h6>
  ),

  strong: ({ children }: any) => (
    <strong className="font-semibold text-slate-900 dark:text-white">{children}</strong>
  ),
  em: ({ children }: any) => <em className="italic">{children}</em>,
  del: ({ children }: any) => <del className="text-slate-400 line-through">{children}</del>,

  // ── lists ───────────────────────────────────────────────────────────────
  ul: ({ children }: any) => (
    <ul className="mb-3 ml-1 list-outside list-disc space-y-1 pl-4 last:mb-0 marker:text-slate-400">
      {children}
    </ul>
  ),
  ol: ({ children }: any) => (
    <ol className="mb-3 ml-1 list-outside list-decimal space-y-1 pl-4 last:mb-0 marker:text-slate-400">
      {children}
    </ol>
  ),
  li: ({ children, className }: any) =>
    // GFM task-list items carry their own checkbox; drop the bullet for those.
    className?.includes("task-list-item") ? (
      <li className="flex list-none items-start gap-2 [&>input]:mt-1">{children}</li>
    ) : (
      <li className="leading-6">{children}</li>
    ),
  input: ({ checked, type }: any) =>
    type === "checkbox" ? (
      <input type="checkbox" checked={!!checked} readOnly className="accent-indigo-600" />
    ) : null,

  // ── links, images, quotes, rules ─────────────────────────────────────────
  a: ({ href, children }: any) => {
    const url = safeUrl(href, SAFE_LINK);
    if (!url) return <span>{children}</span>;
    return (
      <a
        href={url}
        target="_blank"
        // `noopener` closes the reverse-tabnabbing hole on target=_blank.
        rel="noopener noreferrer"
        className="font-medium text-indigo-600 underline underline-offset-2 hover:text-indigo-700 dark:hover:text-indigo-300 dark:text-indigo-400"
      >
        {children}
      </a>
    );
  },
  img: ({ src, alt }: any) => <MarkdownImage src={src} alt={alt} />,

  blockquote: ({ children }: any) => (
    <blockquote className="my-3 border-l-2 border-indigo-300 pl-3 text-slate-600 dark:border-indigo-700 dark:text-slate-300">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-slate-200 dark:border-dark-600" />,

  // ── code ────────────────────────────────────────────────────────────────
  // `pre` renders the whole fenced block, so the nested `code` renderer below
  // only ever styles inline spans.
  pre: ({ children }: any) => {
    const child = Array.isArray(children) ? children[0] : children;
    const className: string = child?.props?.className ?? "";
    const language = /language-([\w-]+)/.exec(className)?.[1];
    const code = String(child?.props?.children ?? "").replace(/\n$/, "");
    return <CodeBlock language={language} code={code} />;
  },
  code: ({ children }: any) => (
    <code className="rounded bg-slate-200/70 px-1 py-0.5 font-mono text-[12px] text-slate-800 dark:bg-dark-700 dark:text-slate-200">
      {children}
    </code>
  ),

  // ── tables (GFM) ────────────────────────────────────────────────────────
  table: ({ children }: any) => (
    // Wide tables scroll in their own container so the chat column never does.
    <div className="my-3 overflow-x-auto rounded-xl border border-slate-200 dark:border-dark-600">
      <table className="w-full border-collapse text-[12.5px]">{children}</table>
    </div>
  ),
  thead: ({ children }: any) => (
    <thead className="bg-slate-100 dark:bg-dark-700">{children}</thead>
  ),
  tbody: ({ children }: any) => (
    <tbody className="divide-y divide-slate-200 dark:divide-dark-600">{children}</tbody>
  ),
  tr: ({ children }: any) => <tr>{children}</tr>,
  th: ({ children, style }: any) => (
    <th
      style={style}
      className="whitespace-nowrap px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200"
    >
      {children}
    </th>
  ),
  td: ({ children, style }: any) => (
    <td style={style} className="px-3 py-2 align-top text-slate-700 dark:text-slate-300">
      {children}
    </td>
  ),
};

const REMARK_PLUGINS = [remarkGfm];

/**
 * Memoised on `content`: a streaming reply re-renders on every chunk, and each
 * render re-parses the whole document.
 */
export const MarkdownMessage = memo(function MarkdownMessage({ content }: { content: string }) {
  return (
    <div className="text-sm leading-6 [overflow-wrap:anywhere]">
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={components}>
        {balanceFences(content)}
      </ReactMarkdown>
    </div>
  );
});

export { balanceFences, safeUrl, SAFE_LINK, SAFE_IMAGE };
